import type { ColumnFilterOption } from '@/components/ui';

/**
 * "From Xero" on the quote and invoice lists — the ONE place its two words and
 * its column key are chosen.
 *
 * ⚠️ **ONE MODULE, TWO PAGES.** `/quotes` and `/invoices` both read the label
 * for their cell and both hand `IMPORTED_FROM_XERO_FILTER_OPTIONS` to
 * `useTableFilters`. Two copies would agree the day they were written and drift
 * on the first correction, and what would drift is whether the word in the CELL
 * means the same thing as the word in the FILTER — a row reading "No" that the
 * "No" option does not return, which is invisible from either screen alone.
 * Same reasoning, and the same split, as `lib/line-project-status.ts` beside
 * `components/documents/LineProjectStatusCell.tsx`.
 *
 * The BADGE is `components/documents/ImportedFromXeroBadge.tsx` and stays the
 * single copy of that; this is the single copy of the COLUMN's vocabulary. Read
 * its header for why `importedFromXeroAt` was stored rather than derived.
 *
 * ══ WHAT "No" ACTUALLY MEANS, WHICH IS NOT WHAT IT LOOKS LIKE ═════════════
 *
 * The owner asked for Yes / No, so Yes / No is what renders. But the two words
 * are narrower than they read, and a reader who takes them at face value will
 * misread the column:
 *
 *   Yes   the Xero history importer wrote this document (migration 130)
 *   No    it was CREATED IN EMERGE — *not* "it is not in Xero"
 *
 * ⚠️ **A document created here and then PUSHED to Xero is in Xero and still
 * reads "No".** The column answers *where did this come from*, never *is this
 * in Xero* — that second question is the `xero` link envelope's, and the
 * envelope cannot answer the first one, because a pushed document carries a
 * link exactly as an imported one does. That is the whole reason the column
 * exists rather than being derived from the link.
 *
 * ⛔ **NO HELPER TEXT SAYS ANY OF THIS ON SCREEN**, and none may be added — the
 * standing prohibition covers prose attached to a control, and a funnel is a
 * control. The distinction lives here, in the source, for whoever next reads or
 * rewords the column.
 *
 * ⛔ **AND THE LABELS ARE NOT THE PLACE TO SMUGGLE IT IN EITHER.** "Imported" /
 * "Created here" would be more precise and is not what was asked for.
 *
 * ⚠️ *SUPERSEDED, the reason that used to be given: "worse, widening the words
 * here silently widens the FILTER OPTION VALUES with them, which is a behaviour
 * change wearing a copy change's clothes."* That was true while the option
 * values WERE the labels; they are the wire's `'true'`/`'false'` now, so
 * rewording is safely cosmetic here. **The prohibition stands on the first
 * ground alone** — it is not what the owner asked for — and the sentence is
 * corrected rather than deleted because a rule whose stated reason has quietly
 * become false is the one the next reader repeals.
 */

/**
 * The column key both pages register with `useTableFilters`.
 *
 * Declared rather than spelled at each call site so `headerFor(...)` and the
 * `ColumnSpec.key` cannot disagree — a mistyped key does not throw, it renders
 * a plain unfilterable header (`useTableFilters` returns `{ label: key }`), so
 * the failure is a funnel that is simply absent.
 */
export const IMPORTED_FROM_XERO_COLUMN = 'importedFromXero';

const YES = 'Yes';
const NO = 'No';

/**
 * The WIRE values, which are also the column's `get` values.
 *
 * They are the two strings `queryBoolean()` parses on the server, because
 * `useTableFilters` puts a chosen option's `value` into the query param
 * verbatim (`use-table-filters.ts:321`) — there is no encoding step between the
 * two, so an option value is a wire value whether or not it looks like one.
 */
const IMPORTED = 'true';
const NOT_IMPORTED = 'false';

/**
 * The label for a document's `importedFromXeroAt` — the CELL's word.
 *
 * Takes the RAW FIELD rather than a pre-computed boolean, deliberately: a
 * caller that has to write `row.importedFromXeroAt !== null` first is a caller
 * that can write `=== null` by accident, and both branches render a confident
 * word rather than failing.
 */
export function importedFromXeroLabel(importedAt: string | null): string {
  return importedAt === null ? NO : YES;
}

/**
 * The column's `get` — the raw value the filter machinery compares and sends.
 *
 * ⚠️ **`get` EMITS THE WIRE VALUE AND `format` TURNS IT INTO THE WORD, and the
 * pair has to be that way round.** `useTableFilters` sends `column.get(row)`'s
 * vocabulary to the server (a chosen option `value`, verbatim) and renders it
 * through `column.format` (`:192`, `:209`). Emitting `'Yes'` here — which an
 * earlier draft of this module did — would send `?importedFromXero=Yes`, and
 * `queryBoolean()` does not accept it: a **422 that empties the table** and
 * reads as "nothing matches".
 *
 * ⚠️ It also has to agree with `IMPORTED_FROM_XERO_FILTER_OPTIONS` below,
 * because with SEVERAL options ticked there is no single-value query to send
 * and the hook narrows the loaded page instead, comparing those same option
 * values against this function's output (`:304-318`). One vocabulary, both
 * paths.
 */
export function importedFromXeroValue(importedAt: string | null): string {
  return importedAt === null ? NOT_IMPORTED : IMPORTED;
}

/**
 * The column's `format` — wire value back to the word, for the cell and for the
 * funnel's popover. Anything unrecognised renders unchanged rather than being
 * coerced to "No": a confident wrong word is worse than a visible odd one.
 */
export function importedFromXeroFormat(value: string): string {
  if (value === IMPORTED) return YES;
  if (value === NOT_IMPORTED) return NO;
  return value;
}

/**
 * The complete option list for the header funnel.
 *
 * ✅ **THE COLUMN IS `scope: 'server'`, `param: IMPORTED_FROM_XERO_COLUMN`, AND
 * THE VALUES ARE THE WIRE'S.** Measured in the backend repo 2026-09-02, after
 * the filter landed: `importedFromXero: queryBoolean().optional()` on BOTH list
 * query schemas — `invoices.schema.ts:542` and `quotes.schema.ts:706` — and
 * `'importedFromXero'` in both `sort` enums (`:660`, `:830`).
 *
 * ⚠️ **SUPERSEDED, AND THE WAY IT WAS WRONG IS WORTH MORE THAN THE FIX:** *"🔴
 * THIS COLUMN MUST BE `scope: 'page'`. THERE IS NO `?importedFromXero=` PARAM,
 * AND WIRING ONE EMPTIES THE TABLE … the values are the LABELS … deliberately
 * NOT `'true'`/`'false'`."* That was **an honest measurement of this API taken a
 * few minutes too early** — the filter was being added in the other repo while
 * this file was being written, and the reading even covered that repo's
 * uncommitted diff, which did not yet contain it.
 *
 * ⚠️ It is the shape the root `CLAUDE.md` records under *"Documenting a file
 * another agent is still editing put a FALSE claim in here"*: the block did not
 * merely go stale, it **pre-refuted the correct answer** in the strongest words
 * available, so a reader who checked the API and found the filter had to decide
 * which of the two to disbelieve. **A dated measurement is a reading taken at a
 * MOMENT, and during concurrent work the moment is the load-bearing part.**
 *
 * ⚠️ **`?importedFromXero=false` GENUINELY NARROWS**, which is why both options
 * are real and neither may be dropped as "no filter". The column is either NULL
 * or a timestamp — no third state — so the two partition the list. Its
 * neighbours on those schemas do NOT behave this way (`unsettled` and `overdue`
 * on invoices, `awaitingSignature` on quotes all treat `false` as "no filter"),
 * and the backend schemas say so at the field.
 *
 * ⛔ **NO `multi`.** `?statusId=`, `?stageId=` and this param each take ONE
 * value; comma-separated multi-value filters are COMPANIES-ONLY across this
 * API, so a CSV here is a 422. With both options ticked the hook sends nothing
 * and narrows the loaded page instead (`use-table-filters.ts:304-318`), which
 * is correct and is why `get` must speak the same vocabulary as these values.
 *
 * ⚠️ **DECLARED RATHER THAN ACCUMULATED**, which is what `ColumnSpec.options`
 * exists for — and a declared list wins for a page-scoped column too. Left to
 * derive itself the popover would offer only what the loaded page happens to
 * hold, so on `/invoices`, where nearly everything is imported, it would often
 * offer "Yes" alone and give no hint that "No" was missing.
 *
 * Order is preserved rather than alphabetised (`useTableFilters` does not sort
 * a declared list). Yes before No, matching how the owner said the pair and how
 * the badge reads — it renders for "Yes" and nothing for "No".
 */
export const IMPORTED_FROM_XERO_FILTER_OPTIONS: ColumnFilterOption[] = [
  { value: IMPORTED, label: YES },
  { value: NOT_IMPORTED, label: NO },
];
